import * as api from "./client";

// ============================================================================
// Types
// ============================================================================

export type DiagnosticStepStatus = "pass" | "fail" | "warn" | "info" | "skipped" | "error";

export type DiagnosticStepKey =
  | "firm_identity"
  | "kill_switch"
  | "features_source"
  | "firm_features"
  | "role_gate"
  | "early_release"
  | "access"
  | "jurisdiction"
  | "rankings"
  | "accolades"
  | "recommendations"
  | "embargo";

/** One decision the account portal makes on the way to the firm's list of rows. */
export type DiagnosticStep = {
  key: DiagnosticStepKey;
  title: string;
  status: DiagnosticStepStatus;
  summary: string;
  /** The raw inputs the decision was made from: ids, lists, flag values. */
  details: Record<string, unknown>;
};

export type DiagnosticRow = {
  kind: "ranking" | "accolade" | "recommendation";
  id: string;
  type: string | null;
  tier: number | null;
  displayName: string;
  practiceArea: string | null;
  countryName: string | null;
  countryId: number | null;
  editionName: string | null;
  editionId: number | null;
  publicationName: string | null;
  publicationStatus: string | null;
  postStatus: string | null;
  /** Listed only because the early-release window is open for its edition. */
  earlyRelease: boolean;
  preReleaseLaunchDate: string | null;
};

export type ExclusionReason =
  | "post_not_published"
  | "publication_not_active"
  | "researching_outside_early_release"
  | "country_not_entitled"
  | "country_missing"
  | "firm_not_entitled"
  | "no_entitled_jurisdictions";

export type ExcludedRow = DiagnosticRow & {
  reasons: ExclusionReason[];
};

export type DiagnosticMember = {
  userId: string;
  name: string | null;
  email: string | null;
  roles: string[];
  hasMarketingRole: boolean;
};

/** Why a subscription does not count toward the countries the firm is entitled to. */
export type SubscriptionReason =
  | "not_complete"
  | "outside_period"
  | "feature_missing"
  | "no_country_region"
  | "country_region_unrecognised";

export type SubscriptionWarning = "uk_single_jurisdiction" | "worldwide_not_recognised";

export type NamedJurisdiction = { countryId: number; name: string | null };

/** One fulfilment subscription of the firm and what it does for the Marketing Toolkit. */
export type DiagnosticSubscription = {
  subscriptionId: string;
  packageId: string;
  packageName: string;
  productCode: string;
  status: string;
  startDate: string;
  endDate: string;
  siteStructureId: string | null;
  placementCountryIds: string[] | null;
  featureCodes: string[];
  carriesFeature: boolean;
  active: boolean;
  countryIds: number[];
  /** The countryIds with their names, in the same order. */
  jurisdictions: NamedJurisdiction[];
  /** Counts toward the countries the firm is entitled to. */
  contributes: boolean;
  reasons: SubscriptionReason[];
  siteStructureName: string | null;
  /** Rows the portal shows because of this subscription. */
  rowsUnlocked: number;
  /** Identical for subscriptions with the same package, status, dates and Country / Region. */
  groupKey: string;
  /** Rows only this group opens: the firm would lose them if these subscriptions ended. */
  onlySourceRowCount: number;
  warnings: SubscriptionWarning[];
};

export type JurisdictionStatus = "shown" | "filter_skipped" | "not_entitled" | "not_covered";

/** A country where the firm holds rankings or accolades; countryId null groups rows with none. */
export type DiagnosticJurisdiction = {
  countryId: number | null;
  /** The Country / Region settings that open this jurisdiction, besides Global. */
  openedBy: { regions: { scopeId: string; name: string }[]; ownCountry: boolean };
  countryName: string | null;
  rowCount: number;
  shownCount: number;
  /** Subscription ids of the subscriptions that open this country. */
  coveredBy: string[];
  status: JurisdictionStatus;
};

/** How an entitlement region compares with the publication guide it belongs to. */
export type RegionMappingEntry = {
  scopeId: string;
  name: string;
  jurisdictions: NamedJurisdiction[];
  guideName: string | null;
  missingFromRegion: NamedJurisdiction[];
  outsideGuide: NamedJurisdiction[];
};

export type DiagnosticFlag = { key: string; enabled: boolean };

/** The settings staff-bff read to reach the decision. */
export type DiagnosticSettings = {
  earlyReleaseDays: number;
  earlyReleaseDaysVariable: "MARKETING_TOOLKIT_EARLY_RELEASE_DAYS";
  /** The user the flags were evaluated for; null when evaluated without one. */
  flagClientId: string | null;
  flags: DiagnosticFlag[];
};

export type FirmDiagnosticsResponse = {
  input: {
    firmRef: string | null;
    organisationId: string | null;
    companyId: string | null;
  };
  firm: {
    companyId: string | null;
    companyName: string | null;
    firmRef: string | null;
    wordpressTermId: number | null;
    businessType: string | null;
    organisations: { id: string; name: string | null }[];
  };
  verdict: {
    /** blocked: the portal answers 403 or 404. no_rows: access is fine but the list is empty. */
    outcome: "rows" | "no_rows" | "blocked";
    summary: string;
  };
  settings: DiagnosticSettings;
  steps: DiagnosticStep[];
  rows: DiagnosticRow[];
  excluded: ExcludedRow[];
  /** error: why fulfilment could not be read; items is then empty. */
  subscriptions: { items: DiagnosticSubscription[]; error: string | null };
  jurisdictions: DiagnosticJurisdiction[];
  regionMapping: RegionMappingEntry[];
  /** null when no organisation is known for the firm. */
  members: DiagnosticMember[] | null;
  generatedAt: string;
};

/** Exactly one key: the backend refuses a request that names the firm more than one way. */
export type FirmDiagnosticsLookup =
  | { firmRef: string }
  | { organisationId: string }
  | { companyId: string };

// ============================================================================
// Service
// ============================================================================

export const marketingFirmDiagnostics = {
  get: async (lookup: FirmDiagnosticsLookup): Promise<FirmDiagnosticsResponse> => {
    const params = new URLSearchParams(lookup);
    return api.get<FirmDiagnosticsResponse>(
      `/v1/marketing-toolkit/firm-diagnostics?${params.toString()}`,
    );
  },
};
